/**
 * Toast — Hệ thống thông báo nhẹ
 * Dùng cho cả internal + commercial
 *
 * Usage:
 *   import { useToast } from './Toast';
 *   const { showToast } = useToast();
 *   showToast('Dịch hoàn tất!', 'success');
 *   showToast('Nghẽn mạng, thử lại nhé', 'error');
 */

import React, { createContext, useContext, useState, useCallback } from 'react';
import { CheckCircle, AlertTriangle, Info, X } from 'lucide-react';

type ToastType = 'success' | 'error' | 'info' | 'reward';

interface Toast {
  id: string;
  message: string;
  type: ToastType;
  action?: { label: string; onClick: () => void };
}

interface ToastContextValue {
  showToast: (message: string, type?: ToastType, action?: Toast['action']) => void;
}

const ToastContext = createContext<ToastContextValue>({ showToast: () => {} });

export const useToast = () => useContext(ToastContext);

const TOAST_DURATION: Record<ToastType, number> = {
  success: 3000,
  error: 5000,
  info: 4000,
  reward: 5000,
};

const TOAST_STYLES: Record<ToastType, { bg: string; icon: React.ElementType; iconColor: string }> = {
  success: { bg: 'bg-emerald-50 border-emerald-200', icon: CheckCircle, iconColor: 'text-emerald-600' },
  error: { bg: 'bg-amber-50 border-amber-200', icon: AlertTriangle, iconColor: 'text-amber-600' },
  info: { bg: 'bg-indigo-50 border-indigo-200', icon: Info, iconColor: 'text-indigo-600' },
  reward: { bg: 'bg-violet-50 border-violet-200', icon: CheckCircle, iconColor: 'text-violet-600' },
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const showToast = useCallback((message: string, type: ToastType = 'info', action?: Toast['action']) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    setToasts(prev => [...prev.slice(-2), { id, message, type, action }]); // max 3

    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, TOAST_DURATION[type]);
  }, []);

  const dismiss = (id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}

      {/* Toast container */}
      <div className="fixed top-4 right-4 z-[999] space-y-2 max-w-sm">
        {toasts.map((toast) => {
          const style = TOAST_STYLES[toast.type];
          const Icon = style.icon;
          return (
            <div
              key={toast.id}
              className={`${style.bg} border rounded-xl px-4 py-3 shadow-lg flex items-start gap-3 animate-in slide-in-from-top-2 fade-in duration-200`}
            >
              <Icon className={`w-5 h-5 ${style.iconColor} shrink-0 mt-0.5`} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-800">{toast.message}</p>
                {toast.action && (
                  <button
                    onClick={() => { toast.action!.onClick(); dismiss(toast.id); }}
                    className="text-xs font-bold text-indigo-600 hover:underline mt-1"
                  >
                    {toast.action.label}
                  </button>
                )}
              </div>
              <button onClick={() => dismiss(toast.id)} className="text-slate-400 hover:text-slate-600 shrink-0">
                <X className="w-4 h-4" />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
};
